/**
 * The locale vocabulary, kept free of every import.
 *
 * `main.ts` resolves the language before Angular boots and must not pull the
 * framework into that path, so these constants live apart from `LocaleService`
 * (which injects HttpClient, AuthService and AuthStore). The service re-exports
 * them, so application code keeps importing from `locale-service`.
 */

/**
 * The languages this interface is translated into.
 *
 * Two, and deliberately not the twelve in `ContentLocale`: an administrator works
 * in German or English. The crew application is the surface that speaks the
 * other ten, and it ships its own catalogues for them.
 */
export type AppLocale = 'de' | 'en';

export const LOCALES: readonly AppLocale[] = ['de', 'en'];

/**
 * The languages a *record* can be kept in.
 *
 * The Gustl lookup lists -- Gebiete, Tour-Typen, Vorwarnungs-Kategorien,
 * Vorwarnungs-Texte, Einsatz-Kategorien and Einsatz-Tätigkeiten -- are written
 * here and read in the crew application, so their names run to every language
 * that application offers even though this one shows only two. The same list is
 * what an administrator picks from when inviting somebody: the language chosen
 * there is the language that person's app and invitation arrive in.
 *
 * `App\Enums\Locale` holds the same twelve on the other side of the wire.
 */
export type ContentLocale =
  'de' | 'en' | 'cs' | 'hu' | 'sk' | 'sl' | 'ro' | 'hr' | 'sr' | 'bs' | 'pl' | 'tr';

export const CONTENT_LOCALES: readonly ContentLocale[] = [
  'de',
  'en',
  'cs',
  'hu',
  'sk',
  'sl',
  'ro',
  'hr',
  'sr',
  'bs',
  'pl',
  'tr',
];

export function isContentLocale(value: unknown): value is ContentLocale {
  return typeof value === 'string' && CONTENT_LOCALES.includes(value as ContentLocale);
}

/**
 * The source language: every `$localize` message is authored in German, so this
 * locale needs no translation file.
 */
export const DEFAULT_LOCALE: AppLocale = 'de';

/**
 * Written by the backend's `LocaleController` and deliberately left
 * unencrypted, so the choice is known before Angular boots.
 */
export const LOCALE_COOKIE_NAME = 'locale';

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && LOCALES.includes(value as AppLocale);
}
